import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useDialog } from "../../../DialogContext";
import ContactMiniForm from "../../Contacts/Contact Forms/ContactMiniForm";
import { pageScrollStyle } from "../../../../styles/styles";
import { getAllFilterContacts } from "../../../../redux/Apps/Lawyerapp/ContactSlice/ContactSlice";
import { getNestedError } from "./InputFields";
import { NoDataOptionField } from "../../../../static/staticdata";
import noOption from "../../../../assets/no-option-found.webp";
import {
  AccountCircleRounded,
  AddRounded,
  BusinessRounded,
} from "@mui/icons-material";
import { Controller } from "react-hook-form";

const normalizeContact = (item) => {
  if (!item || typeof item !== "object" || item.id === undefined) {
    return null;
  }

  return {
    id: item.id,
    first_name: item.first_name || "",
    last_name: item.last_name || "",
    company_name: item.company_name || "",
    contact_type:
      item.contact_type || (item.company_name ? "company" : "person"),
    ...item,
  };
};

const mergeUniqueContacts = (...groups) => {
  const orderedContacts = new Map();

  groups.flat().forEach((item) => {
    const normalized = normalizeContact(item);

    if (!normalized) {
      return;
    }

    orderedContacts.set(normalized.id, {
      ...(orderedContacts.get(normalized.id) || {}),
      ...normalized,
    });
  });

  return Array.from(orderedContacts.values());
};

const dedupeSelectedContacts = (items) => {
  const orderedContacts = new Map();

  (Array.isArray(items) ? items : []).forEach((item) => {
    const normalized = normalizeContact(item);
    if (!normalized?.id) return;
    orderedContacts.set(normalized.id, normalized);
  });

  return Array.from(orderedContacts.values());
};

const getNextPage = (url) => {
  if (!url) return 1;

  const pageMatch = String(url).match(/[?&]page=(\d+)/);
  return pageMatch ? Number.parseInt(pageMatch[1], 10) : 1;
};

const ContactApiField = ({
  name,
  label,
  control,
  value,
  onChange,
  errors,
  token,
  apiUrl,
  isCompany = false,
  isRequired = false,
  renderNoOptions,
  AddcontactFunc,
  formatApiResponse,
  tabName,
  editData,
  multiple = false,
  placeholder = "Select contact",
  returnFullData = false,
  disabled = false,
  refreshKey = 0,
  searchKey = "contact_name",
  teamMemberLawyerId = null,
}) => {
  const dispatch = useDispatch();
  const { showDialog } = useDialog();
  const [apiOptions, setApiOptions] = useState([]);
  const [nextUrl, setNextUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedContact, setSelectedContact] = useState(multiple ? [] : null);
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);
  const nextUrlRef = useRef(null);
  const isLoadingRef = useRef(false);
  const searchTimeoutRef = useRef(null);

  const defaultAddContact = useCallback(
    () =>
      showDialog(
        <ContactMiniForm defaultType={isCompany ? "company" : "person"} />,
        {
          dialogTitle: isCompany ? "Create company" : "Create person",
        },
      ),
    [isCompany, showDialog],
  );

  const handleAddContact = AddcontactFunc || defaultAddContact;

  const defaultFormatApiResponse = useCallback(
    (data) => {
      if (!Array.isArray(data?.res)) {
        return [];
      }

      return data.res
        .map((item) => normalizeContact(item))
        .filter(Boolean);
    },
    [],
  );

  const pinnedOptions = useMemo(() => {
    const normalizeInput = (item) => {
      if (!item) return null;

      if (typeof item === "number" || typeof item === "string") {
        return { id: item };
      }

      return normalizeContact(item);
    };

    const valueItems = multiple
      ? Array.isArray(value)
        ? value.map(normalizeInput)
        : []
      : value
        ? [normalizeInput(value)]
        : [];

    const editItems = Array.isArray(editData)
      ? editData.map(normalizeInput)
      : editData
        ? [normalizeInput(editData)]
        : [];

    return mergeUniqueContacts(editItems, ...valueItems);
  }, [editData, multiple, value]);

  const resolveSelection = useCallback(
    (rawValue) => {
      const resolveSingleOption = (item) => {
        if (item === null || item === undefined || item === "") {
          return null;
        }

        const id = typeof item === "object" ? item.id : item;

        return (
          apiOptions.find((option) => option.id === id) ||
          pinnedOptions.find((option) => option.id === id) ||
          (typeof item === "object" ? normalizeContact(item) : { id })
        );
      };

      if (multiple) {
        if (!Array.isArray(rawValue)) return [];
        return rawValue.map(resolveSingleOption).filter(Boolean);
      }

      return resolveSingleOption(rawValue);
    },
    [apiOptions, pinnedOptions, multiple],
  );
  const fetchContacts = useCallback(
    async (searchQuery = "", isLoadMore = false) => {
      if (!apiUrl || !token) {
        setApiOptions(pinnedOptions);
        setNextUrl(null);
        nextUrlRef.current = null;
        return;
      }

      if (isLoadMore && (!nextUrlRef.current || isLoadingRef.current)) {
        return;
      }

      const currentRequestId = ++requestIdRef.current;
      isLoadingRef.current = true;
      setIsLoading(true);

      try {
        const result = await dispatch(
          getAllFilterContacts({
            url: apiUrl,
            token,
            page: isLoadMore ? getNextPage(nextUrlRef.current) : 1,
            search: searchQuery,
            searchKey,
            teamMemberLawyerId,
          }),
        ).unwrap();

        if (!mountedRef.current || currentRequestId !== requestIdRef.current) {
          return;
        }

        const formatFunction = formatApiResponse || defaultFormatApiResponse;
        const formatted = formatFunction({
          res: result?.results || [],
          tabName,
          api: apiUrl,
        });
        const safeFormatted = Array.isArray(formatted)
          ? formatted.map((item) => normalizeContact(item)).filter(Boolean)
          : [];

        setApiOptions((prev) =>
          mergeUniqueContacts(
            pinnedOptions,
            isLoadMore ? prev : [],
            safeFormatted,
          ),
        );
        const nextPageUrl = result?.next || result?.pagination?.nextPage || null;
        nextUrlRef.current = nextPageUrl;
        setNextUrl(nextPageUrl);
      } catch (error) {
        console.error("Fetch contacts error:", error);

        if (mountedRef.current && currentRequestId === requestIdRef.current) {
          setApiOptions(pinnedOptions);
          setNextUrl(null);
          nextUrlRef.current = null;
        }
      } finally {
        if (mountedRef.current && currentRequestId === requestIdRef.current) {
          isLoadingRef.current = false;
          setIsLoading(false);
        }
      }
    },
    [
      apiUrl,
      defaultFormatApiResponse,
      dispatch,
      formatApiResponse,
      pinnedOptions,
      searchKey,
      tabName,
      teamMemberLawyerId,
      token,
    ],
  );

  const scheduleSearch = useCallback(
    (query) => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        fetchContacts(query, false);
      }, 300);
    },
    [fetchContacts],
  );

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      isLoadingRef.current = false;
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setApiOptions((prev) => mergeUniqueContacts(pinnedOptions, prev));
  }, [pinnedOptions]);

  useEffect(() => {
    setSearchText("");
    fetchContacts("", false);
  }, [apiUrl, fetchContacts, refreshKey, teamMemberLawyerId, token]);

  const handleInputChange = useCallback(
    (_, input, reason) => {
      if (reason === "input") {
        const nextSearch = input || "";
        setSearchText(nextSearch);
        scheduleSearch(nextSearch);
      }

      if (reason === "clear") {
        setSearchText("");
        scheduleSearch("");
      }
    },
    [scheduleSearch],
  );

  const handleScroll = useCallback(
    (event) => {
      const node = event.currentTarget;

      if (
        !isLoading &&
        nextUrl &&
        node.scrollTop + node.clientHeight >= node.scrollHeight - 50
      ) {
        fetchContacts(searchText, true);
      }
    },
    [fetchContacts, isLoading, nextUrl, searchText],
  );

  const getOptionLabel = useCallback((option) => {
    if (!option) return "";

    if (option.company_name) {
      return option.company_name;
    }

    const fullName =
      `${option.first_name || ""} ${option.last_name || ""}`.trim();

    return fullName || option.company_name || `Contact ${option.id}`;
  }, []);

  const formatOutputValue = useCallback(
    (nextValue) => {
      if (multiple) {
        const selectedValues = Array.isArray(nextValue) ? nextValue : [];

        return returnFullData
          ? selectedValues
          : selectedValues.map((item) => item?.id).filter(Boolean);
      }

      return returnFullData ? nextValue || null : nextValue?.id || null;
    },
    [multiple, returnFullData],
  );

  const handleSelectionChange = useCallback(
    (_, nextValue, fieldOnChange) => {
      const normalizedValue = multiple
        ? dedupeSelectedContacts(nextValue)
        : nextValue || null;

      if (!control && value === undefined) {
        setSelectedContact(normalizedValue || (multiple ? [] : null));
      }

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      setSearchText("");

      const formattedValue = formatOutputValue(normalizedValue);

      if (typeof fieldOnChange === "function") {
        fieldOnChange(formattedValue);
      }

      if (onChange) {
        onChange(formattedValue);
      }
    },
    [control, formatOutputValue, multiple, onChange, value],
  );

  const handleOpen = useCallback(() => {
    if (!apiOptions.length && !isLoading) {
      fetchContacts(searchText, false);
    }
  }, [apiOptions.length, fetchContacts, isLoading, searchText]);

  const fieldError = getNestedError(errors, name);

  const ListboxComponent = React.forwardRef(function ContactListbox(
    props,
    ref,
  ) {
    const { children, ...rest } = props;

    return (
      <Box ref={ref} sx={{ position: "relative" }}>
        <Box
          {...rest}
          sx={{
            ...pageScrollStyle,
            maxHeight: "250px",
            overflowY: "auto",
            pb: handleAddContact ? 6 : 0,
          }}
          onScroll={handleScroll}
        >
          {children}
        </Box>
        {handleAddContact && (
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              borderTop: "1px solid #e5e5e5",
              backgroundColor: "#fff",
              p: 1,
              display: "flex",
              justifyContent: "flex-end",
            }}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            <Button
              variant="text"
              size="small"
              color="primary"
              endIcon={<AddRounded />}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                handleAddContact();
              }}
            >
              {isCompany ? "Add Company" : "Add Person"}
            </Button>
          </Box>
        )}
      </Box>
    );
  });

  const renderInput = useCallback(
    (params, currentValue) => {
      const selectedValue =
        !multiple &&
        currentValue &&
        typeof currentValue === "object" &&
        !Array.isArray(currentValue)
          ? currentValue
          : null;

      return (
        <TextField
          {...params}
          label={
            <span>
              {label}
              {isRequired && <span style={{ color: "red" }}> *</span>}
            </span>
          }
          placeholder={placeholder}
          size="small"
          fullWidth
          error={Boolean(fieldError)}
          helperText={fieldError?.message}
          inputProps={{ ...params.inputProps, name }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                {selectedValue ? (
                  <Chip
                    size="small"
                    sx={{
                      bgcolor:
                        selectedValue?.contact_type === "company"
                          ? "#2e77f5"
                          : "#bf3fe1",
                      color: "#fff",
                      mr: 0.75,
                    }}
                    label={
                      selectedValue?.contact_type === "company" ? (
                        <BusinessRounded
                          sx={{ color: "#fff", fontSize: "0.95rem" }}
                        />
                      ) : (
                        <AccountCircleRounded
                          sx={{ color: "#fff", fontSize: "0.95rem" }}
                        />
                      )
                    }
                  />
                ) : null}
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="primary" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      );
    },
    [fieldError, isLoading, isRequired, label, multiple, name, placeholder],
  );

  const commonProps = {
    multiple,
    limitTags: multiple ? 2 : undefined,
    filterSelectedOptions: multiple,
    fullWidth: true,
    disabled,
    options: apiOptions,
    loading: isLoading,
    getOptionLabel,
    isOptionEqualToValue: (option, selectedValue) =>
      option?.id === selectedValue?.id,
    noOptionsText: renderNoOptions || (
      <NoDataOptionField
        imgSrc={noOption}
        text={
          isLoading
            ? "Loading..."
            : `Don't have any ${isCompany ? "company" : "person"}`
        }
        btnText={isCompany ? "Company" : "Person"}
        handleClickNoData={handleAddContact}
        endIcon={<AddRounded />}
      />
    ),
    inputValue: searchText,
    onInputChange: handleInputChange,
    onOpen: handleOpen,
    clearOnBlur: false,
    ListboxComponent,
    ListboxProps: {
      onScroll: handleScroll,
      style: { maxHeight: "250px", overflowY: "auto" },
    },
    renderTags: (tagValue, getTagProps) =>
      tagValue.map((option, index) => {
        const { key, ...tagProps } = getTagProps({ index });

        return (
          <Chip
            {...tagProps}
            key={key}
            size="small"
            label={getOptionLabel(option)}
            sx={{
              height: 24,
              fontSize: "0.72rem",
              maxWidth: 140,
              bgcolor:
                option?.contact_type === "company" ? "#2e77f515" : "#bf3fe115",
              color:
                option?.contact_type === "company" ? "#2e77f5" : "#bf3fe1",
              border: `1px solid ${
                option?.contact_type === "company" ? "#2e77f530" : "#bf3fe130"
              }`,
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                px: 0.75,
              },
            }}
          />
        );
      }),
    renderOption: (props, option) => (
      <li
        {...props}
        key={option.id}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="body1">{getOptionLabel(option)}</Typography>
        <Box>
          {option?.contact_type === "company" ? (
            <BusinessRounded sx={{ fontSize: "0.9rem", color: "#2e77f5" }} />
          ) : (
            <AccountCircleRounded
              sx={{ fontSize: "0.9rem", color: "#bf3fe1" }}
            />
          )}
        </Box>
      </li>
    ),
  };

  if (control && name) {
    return (
      <Controller
        name={name}
        control={control}
        rules={isRequired ? { required: `Please fill ${label}` } : {}}
        render={({ field }) => {
          const controlValue = resolveSelection(field.value);

          return (
            <Autocomplete
              {...commonProps}
              value={controlValue}
              onChange={(event, nextValue) =>
                handleSelectionChange(event, nextValue, field.onChange)
              }
              renderInput={(params) => renderInput(params, controlValue)}
            />
          );
        }}
      />
    );
  }

  const resolvedValue =
    value !== undefined ? resolveSelection(value) : selectedContact;

  return (
    <FormControl fullWidth size="small">
      <Autocomplete
        {...commonProps}
        value={resolvedValue}
        onChange={handleSelectionChange}
        renderInput={(params) => renderInput(params, resolvedValue)}
      />
    </FormControl>
  );
};

export default ContactApiField;
